/**
 * Customer Registration Action
 * Triggered by: arc:action:customerRegister
 */

export const runCustomerRegister = async (data = {}) => {
    try {
        const { 
            firstName, 
            lastName, 
            email, 
            password,
            passwordConfirmation,
            phone = '',
            acceptsMarketing = false,
            form,
            returnUrl = null,
            redirectOnSuccess = true 
        } = data;

        // Validation
        if (!firstName || !lastName || !email || !password) {
            throw new Error('First name, last name, email, and password are required');
        }

        if (password !== passwordConfirmation) {
            throw new Error('Passwords do not match');
        }

        // Email validation
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailRegex.test(email)) {
            throw new Error('Please enter a valid email address');
        }

        // Password strength validation
        if (password.length < 6) {
            throw new Error('Password must be at least 6 characters long');
        }

        // Prepare form data
        const formData = new FormData();
        formData.append('form_type', 'create_customer');
        formData.append('utf8', '✓');
        formData.append('customer[first_name]', firstName);
        formData.append('customer[last_name]', lastName);
        formData.append('customer[email]', email);
        formData.append('customer[password]', password);
        
        if (phone) {
            formData.append('customer[phone]', phone);
        }
        
        if (acceptsMarketing) {
            formData.append('customer[accepts_marketing]', '1');
        }
        
        if (returnUrl) {
            formData.append('return_url', returnUrl);
        }

        // Dispatch registration attempt event
        window.dispatchEvent(new CustomEvent('customer:registrationAttempt', {
            detail: {
                firstName,
                lastName,
                email,
                phone,
                acceptsMarketing,
                returnUrl
            }
        }));

        // Submit registration request
        const response = await fetch('/account', {
            method: 'POST',
            body: formData,
            headers: {
                'Accept': 'application/json, text/html',
            }
        });

        if (response.ok) {
            // Check if response is JSON (error) or HTML (success redirect)
            const contentType = response.headers.get('content-type');
            
            if (contentType && contentType.includes('application/json')) {
                // JSON response usually means validation errors
                const errorData = await response.json();
                const errors = errorData.errors || {};
                const errorMessages = Object.values(errors).flat();
                throw new Error(errorMessages.join(', ') || 'Registration failed');
            } else {
                // HTML response usually means success
                window.dispatchEvent(new CustomEvent('customer:registrationSuccess', {
                    detail: {
                        firstName,
                        lastName,
                        email,
                        phone,
                        acceptsMarketing,
                        redirectUrl: returnUrl || '/account',
                        timestamp: Date.now()
                    }
                }));

                if (redirectOnSuccess) {
                    const redirectUrl = returnUrl || '/account';
                    window.location.href = redirectUrl;
                }

                return {
                    success: true,
                    firstName,
                    lastName,
                    email,
                    redirectUrl: returnUrl || '/account',
                    message: 'Registration successful'
                };
            }
        } else {
            // Handle HTTP errors
            let errorMessage = 'Registration failed';
            
            try {
                const errorText = await response.text();
                // Try to extract error message from HTML response
                const parser = new DOMParser();
                const doc = parser.parseFromString(errorText, 'text/html');
                const errorElements = doc.querySelectorAll('.errors li, .error, [data-error]');
                
                if (errorElements.length > 0) {
                    const errors = Array.from(errorElements).map(el => el.textContent.trim());
                    errorMessage = errors.join(', ');
                }
            } catch (e) {
                console.error('Error parsing response:', e);
            }

            throw new Error(errorMessage);
        }

    } catch (error) {
        console.error('Customer Registration error:', error);
        
        window.dispatchEvent(new CustomEvent('customer:registrationError', {
            detail: {
                error: error.message,
                email: data.email
            }
        }));

        return {
            success: false,
            error: error.message
        };
    }
};

// Initialize registration form listeners
export const initializeCustomerRegistration = (config = {}) => {
    const {
        formSelector = '[data-customer-registration-form]',
        autoSetupForms = true,
        enablePasswordStrengthCheck = true
    } = config;

    if (autoSetupForms) {
        setupRegistrationForms(formSelector, enablePasswordStrengthCheck);
    }

    window.dispatchEvent(new CustomEvent('customerRegistration:initialized', {
        detail: { config, timestamp: Date.now() }
    }));
};

// Setup registration form event listeners
const setupRegistrationForms = (formSelector, enablePasswordStrengthCheck) => {
    const forms = document.querySelectorAll(formSelector);
    
    forms.forEach(form => {
        form.removeEventListener('submit', handleRegistrationSubmit);
        form.addEventListener('submit', handleRegistrationSubmit);

        // Add password strength checking if enabled
        if (enablePasswordStrengthCheck) {
            const passwordField = form.querySelector('[name="customer[password]"]');
            const confirmField = form.querySelector('[name="customer[password_confirmation]"]');
            
            if (passwordField) {
                passwordField.removeEventListener('input', handlePasswordStrengthCheck);
                passwordField.addEventListener('input', handlePasswordStrengthCheck);
            }
            
            if (confirmField) {
                confirmField.removeEventListener('input', handlePasswordConfirmation);
                confirmField.addEventListener('input', handlePasswordConfirmation);
            }
        }
    });
};

// Handle registration form submissions
const handleRegistrationSubmit = async (event) => {
    event.preventDefault();
    
    const form = event.target;
    const formData = new FormData(form);
    
    const firstName = formData.get('customer[first_name]');
    const lastName = formData.get('customer[last_name]');
    const email = formData.get('customer[email]');
    const password = formData.get('customer[password]');
    const passwordConfirmation = formData.get('customer[password_confirmation]');
    const phone = formData.get('customer[phone]');
    const acceptsMarketing = formData.get('customer[accepts_marketing]') === '1';
    const returnUrl = formData.get('return_url') || form.dataset.returnUrl;

    // Disable submit button
    const submitBtn = form.querySelector('[type="submit"]');
    const originalText = submitBtn?.textContent;
    if (submitBtn) {
        submitBtn.disabled = true;
        submitBtn.textContent = 'Creating account...';
    }

    try {
        await window.dispatchEvent(new CustomEvent('arc:action:customerRegister', {
            detail: {
                firstName,
                lastName,
                email,
                password,
                passwordConfirmation,
                phone,
                acceptsMarketing,
                returnUrl,
                form,
                redirectOnSuccess: true
            }
        }));
    } finally {
        // Re-enable submit button
        if (submitBtn) {
            submitBtn.disabled = false;
            submitBtn.textContent = originalText;
        }
    }
};

// Password strength checking
const handlePasswordStrengthCheck = (event) => {
    const password = event.target.value;
    const strength = checkPasswordStrength(password);
    
    window.dispatchEvent(new CustomEvent('customer:passwordStrengthCheck', {
        detail: {
            password,
            strength,
            isValid: strength.score >= 3
        }
    }));
};

// Password confirmation checking
const handlePasswordConfirmation = (event) => {
    const form = event.target.closest('form');
    const passwordField = form.querySelector('[name="customer[password]"]');
    const confirmPassword = event.target.value;
    const password = passwordField?.value || '';
    
    const matches = password === confirmPassword;
    
    window.dispatchEvent(new CustomEvent('customer:passwordConfirmationCheck', {
        detail: {
            matches,
            password,
            confirmPassword
        }
    }));
};

// Password strength checker
const checkPasswordStrength = (password) => {
    const checks = {
        length: password.length >= 8,
        lowercase: /[a-z]/.test(password),
        uppercase: /[A-Z]/.test(password),
        numbers: /\d/.test(password),
        symbols: /[^A-Za-z0-9]/.test(password)
    };
    
    const score = Object.values(checks).filter(Boolean).length;
    
    let level = 'weak';
    if (score >= 4) level = 'strong';
    else if (score >= 3) level = 'medium';
    
    return {
        score,
        level,
        checks,
        isValid: score >= 3
    };
};

export default runCustomerRegister;
